import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useStanRouter } from '@/hooks/useStanRouter';
import { updateSocialLinks } from '@/lib/api/profile';
import { stanAnalytics, stanNotify } from '@/lib';
import { socialLinksSchema } from '@/features/profile/validations/profileSchema';

// Wrap the social links schema with a 'socials' key to match the form structure
const socialLinksFormSchema = z.object({
    socials: socialLinksSchema,
});

type SocialFormData = z.infer<typeof socialLinksFormSchema>;

export function useSocialLinksForm() {
    const { push } = useStanRouter();
    const [showMore, setShowMore] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<SocialFormData>({
        resolver: zodResolver(socialLinksFormSchema),
        defaultValues: {
            socials: {
                instagram: '',
                tiktok: '',
                email: '',
                facebook: '',
                youtube: '',
                link: '',
                pinterest: '',
                twitter: '',
                spotify: '',
                'apple-podcasts': '',
                etsy: '',
                discord: '',
                snapchat: '',
                twitch: '',
                vimeo: '',
                linkedin: '',
            },
        },
    });

    const onSubmit = handleSubmit(data => {
        // Filter out empty values
        const filteredSocials = Object.entries(data.socials).reduce(
            (acc, [key, value]) => {
                if (value && value.trim()) {
                    acc[key] = value.trim();
                }
                return acc;
            },
            {} as Record<string, string>
        );

        // Fire and forget - matching Vue behavior for faster UX
        // The API call happens in parallel while user proceeds to next step
        // This is acceptable here because:
        // 1. Social links are optional
        // 2. User can always update them later in profile settings
        // 3. Faster perceived performance
        updateSocialLinks(filteredSocials).catch(error => {
            stanNotify({
                type: 'error',
                title: 'Failed to save your socials',
                text: 'Please try again',
                error: {
                    message: error instanceof Error ? error.message : 'Unknown error saving social links',
                },
            });
        });

        // Navigate immediately (don't wait for API)
        push('/subscribe/plan');
    });

    const handleSkip = () => {
        stanAnalytics('subscribe-socials-skip');
        push('/subscribe/plan');
    };

    const toggleShowMore = () => {
        setShowMore(prev => !prev);
    };

    return {
        control,
        errors,
        showMore,
        onSubmit,
        handleSkip,
        toggleShowMore,
    };
}
